import { createSelector } from 'reselect';

import { getFormSyncErrors, getFormValues, isPristine as getIsPristine } from 'redux-form';

import get from 'lodash/get';
import find from 'lodash/find';
import getFP from 'lodash/fp/get';

import addDays from 'date-fns/addDays';
import startOfToday from 'date-fns/startOfToday';
import addMonths from 'date-fns/addMonths';

const initialValues = {
  nights: 7,
  flexNights: false,
  adults: 2,
  childrenAges: [],
  dates: {
    startDate: null,
    endDate: null,
  },
  departure: null,
  destination: null,
};

const ALLOWED_MONTH_INTERVAL = 9;
const CLOSEST_START_DATE_INTERVAL = 2;
const minDate = addDays(startOfToday(), CLOSEST_START_DATE_INTERVAL);
const maxDate = addMonths(minDate, ALLOWED_MONTH_INTERVAL);

const getDepartureSuggestions = (state) => {
  const departureCities = get(state, 'tours.searchForm.departure.cities.data', []);
  const departureQuery = get(state, 'tours.searchForm.departure.query', '');

  if (departureQuery) {
    return departureCities.filter(({ name = '' }) => {
      return name.toLowerCase().indexOf(departureQuery.toLowerCase()) === 0;
    });
  }

  return departureCities;
};

const getDestinationSuggestions = (state) => {
  return get(state, 'tours.searchForm.destination.suggestions', []);
};

const isDepartureLoading = getFP('tours.searchForm.departure.cities.loading');
const isDestinationLoading = (state) => {
  const countriesAreLoading = get(state, 'tours.searchForm.destination.countries.loading');
  const suggestionsAreLoading = get(state, 'tours.searchForm.destination.request.loading');

  return countriesAreLoading || suggestionsAreLoading;
};

const getDepartureData = (getSearchFormValues) => (state) => {
  const departureCities = get(state, 'tours.searchForm.departure.cities.data', []);
  const searchFormValues = getSearchFormValues(state) || {};

  return find(departureCities, (city) => city.id === searchFormValues.departure);
};

const getDestinationData = (getSearchFormValues) => (state) => {
  const destinationCountries = get(state, 'tours.searchForm.destination.countries.data', []);
  const destinationSuggestions = get(state, 'tours.searchForm.destination.suggestions', []);
  const searchFormValues = getSearchFormValues(state) || {};

  // поиск в справочниках по айдишнику или исо коду
  const findDestinationObject = (objectList) =>
    find(objectList, (el) => [el.id, el.code].includes(searchFormValues.destination));

  return (
    findDestinationObject(destinationCountries) || findDestinationObject(destinationSuggestions)
  );
};

export default (formName, getFormState) => {
  const getSearchFormValues = getFormState
    ? getFormValues(formName, getFormState)
    : getFormValues(formName);

  return createSelector(
    [
      getDepartureSuggestions,
      getDestinationSuggestions,

      isDepartureLoading,
      isDestinationLoading,

      getDepartureData(getSearchFormValues),
      getDestinationData(getSearchFormValues),

      getFP('tours.searchForm.destination.query'),
      getFP('tours.searchForm.departure.query'),

      getFP('tours.searchForm.dates.data'),
      getFP('tours.searchForm.dates.loading'),

      getSearchFormValues,
      getFormSyncErrors(formName, getFormState),
      getIsPristine(formName, getFormState),
    ],
    (
      departureSuggestions,
      destinationSuggestions,
      departureIsLoading,
      destinationIsLoading,
      departureData,
      destinationData,
      destinationQuery,
      departureQuery,
      datesMap,
      datesAreLoading,
      formValues,
      formErrors,
      isPristine
    ) => {
      return {
        departureSuggestions,
        destinationSuggestions,

        departureIsLoading,
        destinationIsLoading,

        destinationQuery,
        departureQuery,

        departureData,
        destinationData,

        datesMap,
        datesAreLoading,

        formValues,
        formErrors,
        isPristine,
        initialValues,

        minDate,
        maxDate,
      };
    }
  );
};
